// ── המדבקה שיוצאת בסיום תהליך ────────────────────────────────────────────
//
// אלי, 13/09/2026: „איפה נדרשת מדבקה להדפסה אחרי סיום תהליך… סריקת כל
// מדבקה פותחת את הפרויקט והשרטוט בתצוגת עמדת העובד.“
//
// הכלל נקבע במסך ההגדרות; כאן הוא מודפס. שני חלקים בכוונה: התהליכים
// משתנים, והכללים משתנים בלי לגעת בקוד ההדפסה.
//
// ── הברקוד הוא אותו ברקוד ────────────────────────────────────────────────
//
// `partNumber|version` — המטען שנחתם על השרטוט, וזה שהעמדה כבר פותחת.
// כשאין חלק (ניפוק פריט מדף) הוא המק״ט, כפי שהוא בקישור הברקוד. **אין
// מרחב קודים שני** שצריך ללמד סורק, ולכן גם אין מצב שבו מדבקה נסרקת
// ולא קורה כלום.
//
// ── ולמה @page ולא CSS רגיל ──────────────────────────────────────────────
//
// בלי `@page size` הדפדפן מניח A4, והמדפסת מזינה גליל שלם על מדבקה אחת.
// זה נלמד בדם במדבקות המדף — ראה את ההערות ב-`shelfLabel.ts`.

import { qrModules, qrSvgSrc } from './labelCode.js';

export type ProcessLabelRule = {
  process: string;
  mode: 'OFF' | 'OPTIONAL' | 'AUTO';
  fields: string[];
  copies: number;
  widthMm: number;
  heightMm: number;
  available: { key: string; label: string }[];
};

/** הערכים בפועל לתהליך שהסתיים. מפתח שאין לו ערך פשוט אינו מודפס. */
export type ProcessLabelData = Record<string, string | number | null | undefined> & {
  /** מה נכנס לברקוד. ריק = אין ברקוד, גם אם הוא נבחר בכללים. */
  barcode?: string | null;
};

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));

/**
 * כתובת תמונת הקוד — QR בווקטור (אלי, 29/09/2026: „יש לי סורק QR").
 * אותו origin, כדי שהעוגייה תיסע איתה; ראה `labelCode` למה QR ולמה SVG.
 */
export const labelBarcodeSrc = qrSvgSrc;

/**
 * האם יש בכלל מה להדפיס.
 *
 * מצב דלוק עם אפס ערכים בפועל אינו מדבקה ריקה — הוא באג שהיה מוציא נייר
 * לבן מהמדפסת בכל חיתוך.
 */
export function labelHasContent(rule: ProcessLabelRule, input: ProcessLabelData | ProcessLabelData[]): boolean {
  if (rule.mode === 'OFF') return false;
  const list = Array.isArray(input) ? input : [input];
  return list.some((data) =>
    rule.fields.some((k) => (k === 'barcode' ? !!data.barcode : data[k] != null && data[k] !== '')));
}

// ── כמה שורות באמת נכנסות ────────────────────────────────────────────────
//
// אלי, 22/09/2026, עם צילום של מדבקת 50×30 מהמדפסת: „קשה לראות מה כתוב",
// ואז „**אני לא רוצה מדבקה גדולה יותר. אני רוצה מדבקה ברורה יותר ב-50×30
// וב-40×20. יש לך מספיק מקום.**"
//
// ⚠ הפריסה הקודמת נתנה לכל שדה שורה משלו, וגזרה את הגופן מ**גובה
// המדבקה** בלבד — `h * 0.095` לכותרת ו-`h * 0.068` לשאר. על 50×30 עם שישה
// שדות זה יצא 2.85 מ״מ ו-2.1 מ״מ, כלומר הכל באותו גודל זעיר; ועל 40×20 זה
// גלש מהמדבקה בשקט, כי אף חישוב לא שאל **כמה שורות** יש.
//
// שני דברים תוקנו כאן, ושניהם נמדדו ולא נוחשו.

/**
 * שורה מאוחדת לא תעלה על זה.
 *
 * ⚠ והיה כאן גם קצב שני — „כל צד לא יותר מ-13 תווים" — והוא היה שגוי:
 * „משולש · SS304 · 1.5 מ״מ" הוא 23 תווים שנכנסים בנוחות, אבל הצד השני
 * (15 תווים) נפסל לבדו והשורה נשארה מפוצלת. האורך המאוחד הוא הדבר היחיד
 * שבאמת מגביל, ולכן הוא הכלל היחיד.
 */
const ROW_MAX = 26;

/**
 * שורות ההדפסה — לא שדה לשורה.
 *
 * ⚠ **זה מה שקונה לכותרת את גודלה.** על מדבקת לייזר יושבים „בחיתוך 3/12"
 * ו„כמות: 6" — שני ערכים קצרצרים ששתי שורות נפרדות מבזבזות עליהם שליש
 * מגובה המדבקה. איחודם לשורה אחת מוריד חמש שורות לשלוש, ומכפיל את הגופן
 * של המק״ט — שהוא מה שקוראים מרחוק.
 *
 * רק ערכים קצרים מתאחדים, ורק כשהתוצאה עדיין נכנסת: שם חלק ארוך נשאר
 * לבדו, ושורה מאוחדת שהייתה נחתכת גרועה משתי שורות שלמות.
 */
/**
 * הכותרת ארוכה מדי — נשברת, ורק בנקודה.
 *
 * אלי, 23/09/2026: „**מקסימום 14 תווים. אם יש יותר רד שורה.**"
 *
 * ── ⚠ ולמה בנקודה ולא בתו ה-14 ─────────────────────────────────────────
 *
 * `260169.02.04.01.01` נשבר בתו ה-14 היה נותן `260169.02.04.0` ומתחתיו
 * `1.01` — שני מספרים שנראים תקינים לגמרי ואינם אומרים את מה שהם אומרים.
 * מק״ט אינו מחרוזת; הוא מקטעים. שבירה באמצע מקטע היא איך שחלק נלקט לא
 * נכון מהערימה.
 *
 * ⚠ והנקודה **נשארת בסוף השורה** — היא מה שאומר „ממשיך למטה". בלעדיה
 * `260169.02.04` ו-`01.01` נקראים כשני מספרים נפרדים.
 *
 * מקטע יחיד שארוך מהמגבלה נשאר שלם וגולש: אין בו נקודה לשבור בה, וחיתוכו
 * גרוע מגלישה.
 */
export const HERO_MAX_CHARS = 14;

export function splitHero(value: string, max = HERO_MAX_CHARS): string[] {
  const v = value.trim();
  if (v.length <= max) return [v];
  const segs = v.split('.');
  const lines: string[] = [];
  let cur = '';
  for (let i = 0; i < segs.length; i += 1) {
    const seg = segs[i]!;
    const isLast = i === segs.length - 1;
    // הנקודה המפרידה נספרת בשורה הנוכחית, כי היא נשארת בה.
    const candidate = cur === '' ? seg : `${cur}.${seg}`;
    const withDot = isLast ? candidate : `${candidate}.`;
    if (withDot.length <= max || cur === '') {
      cur = candidate;
    } else {
      lines.push(`${cur}.`);
      cur = seg;
    }
  }
  if (cur !== '') lines.push(cur);
  return lines;
}

const visibleLen = (s: string): number => s.replace(/[⁦-⁩]/g, '').length;

export function labelRows(values: string[]): string[] {
  const rows: string[] = [];
  // ⚠ הערך הראשון הוא הכותרת, והוא היחיד שנשבר: „מקסימום 14 תווים"
  // (אלי, 23/09/2026). השאר מתאחדים כרגיל.
  const expanded = values.length > 0
    ? [...splitHero(values[0]!), ...values.slice(1)]
    : values;
  const heroLines = values.length > 0 ? splitHero(values[0]!).length : 0;
  for (const v of expanded) {
    const prev = rows[rows.length - 1];
    // הכותרת לעולם אינה מתאחדת עם דבר — היא לבדה, וגדולה.
    // ⚠ `heroLines` ולא `2`: כשהכותרת נשברה לשתי שורות, השורה שאחריה
    // היא במקום 2 — ואיחודה עם החצי השני של המק״ט היה מדביק „01.01"
    // ל„בחיתוך 1/4". המספר קבוע היה נכון רק כשהכותרת הייתה תמיד שורה אחת.
    // ⚠ תווי בידוד (U+2066–2069) אינם תופסים מקום — הם נספרים כאן כאילו
    // היו, והיו מפצלים את „שם · חומר" לשתי שורות ומקטינים את כל המדבקה.
    const canJoin = rows.length >= heroLines + 1 && prev != null
      && visibleLen(prev) + visibleLen(v) + 3 <= ROW_MAX;
    if (canJoin) rows[rows.length - 1] = `${prev} · ${v}`;
    else rows.push(v);
  }
  return rows;
}

/**
 * הגופן, מהגובה **ומהרוחב** — ומכמה שורות יש בפועל.
 *
 * `hero` מקבל פי 1.7 משורה רגילה: זו היררכיה שנקראת מרחוק, ולא שלוש
 * מדרגות שהן רעש על 20 מ״מ. והתקרה לפי הרוחב היא מה שמנע מהמק״ט להיחתך —
 * תו חד-רוחבי הוא כ-0.62 מגובה הגופן, ולכן זה הגדול ביותר שנכנס בשורה.
 */
export function labelType(
  widthMm: number, heightMm: number, rowCount: number, heroChars: number, hasBarcode: boolean,
  /**
   * כמה שורות תופסת הכותרת.
   *
   * ⚠ נוסף 23/09/2026 עם „מקסימום 14 תווים": כותרת שנשברה לשתיים תופסת
   * פי שניים גובה, וחישוב שמניח שורה אחת היה מגלישה מהמדבקה בשקט — בדיוק
   * הכשל שכבר קרה כאן פעם אחת כשהחישוב לא שאל כמה שורות יש.
   */
  heroRows = 1,
): { pad: number; bcH: number; f1: number; f2: number } {
  const pad = Math.max(1, Math.round(Math.min(widthMm, heightMm) * 0.06));
  // רצועת ברקוד רחבה ונמוכה — הצורה שסורק קורא בה הכי טוב (ראה `labelPrint`).
  const bcH = hasBarcode ? Math.max(6, Math.round(heightMm * 0.3)) : 0;
  const gap = hasBarcode ? 1 : 0;
  const textH = Math.max(3, heightMm - pad * 2 - bcH - gap);
  const others = Math.max(0, rowCount - heroRows);

  // ── הגובה ─────────────────────────────────────────────────────────────
  //
  // תיבת שורה = הגופן × ריווח השורות: 1.1 לכותרת, 1.2 לשאר. הכותרת פי 1.7
  // משורה רגילה, ולכן אם `u` הוא גופן שורה רגילה:
  //
  //     textH = 1.1·(1.7u) + 1.2·u·(שורות-1)
  //
  // ⚠ והגרסה הראשונה חילקה את textH ב-(1.7 + שורות-1) — כלומר חישבה כאילו
  // **כל** שורה בגודל הכותרת. היא החזירה 2.9 מ״מ במקום 6.4, כלומר כמעט
  // בדיוק את הגופן הישן שאלי צילם והתלונן עליו. נתפס במבחן.
  const u = textH / (1.87 * heroRows + 1.2 * others);
  // ── והרוחב ────────────────────────────────────────────────────────────
  //
  // תו חד-רוחבי הוא כ-0.62 מגובה הגופן. בלי התקרה הזאת מק״ט בן 17 תווים
  // דורש יותר מרוחב המדבקה ונחתך באמצע — הצד השני של „קשה לראות מה כתוב".
  const byWidth = (widthMm - pad * 2) / (0.62 * Math.max(6, heroChars));
  // ⚠ כלפי מטה ולא לעיגול הקרוב. `Math.round` העלה 4.3557 ל-4.4, וזה חרג
  // מרוחב המדבקה ב-0.33 מ״מ — כלומר בדיוק ביטל את התקרה שזה עתה חושבה.
  // עשירית מ״מ של גופן אינה נראית לעין; מק״ט חתוך כן.
  const f1 = Math.max(1.8, Math.floor(Math.min(1.7 * u, byWidth) * 10) / 10);
  // ⚠ וכשהרוחב הוא שקבע, נשאר גובה פנוי — ואין סיבה שהשורות הקטנות לא
  // ייקחו אותו. התקרה 1/1.4 היא מה ששומר על ההיררכיה: בלעדיה השורות
  // הקטנות היו משתוות לכותרת, ואז אין „מה שקוראים מרחוק".
  // ── ⚠ והחלל הלבן שנשאר כשהרוחב הוא שקבע ─────────────────────────────
  //
  // נמדד 23/09/2026 על מדבקת לייזר אמיתית: המק״ט הוגבל ל-3.7 מ״מ בגלל
  // רוחב, בזמן שלגובה היה מקום ל-6.4 — **5.7 מ״מ מתוך 16 התבזבזו** כפס
  // לבן באמצע. אלי: „יש עוד מלא מקום."
  //
  // השורות המשניות לוקחות את מה שנשאר, והתקרה 1/1.25 (במקום 1/1.4) היא
  // מה ששומר שהמק״ט יישאר הגדול ביותר — שהוא מה שקוראים מרחוק.
  const f2raw = others > 0
    ? Math.min((textH - 1.1 * f1 * heroRows) / (1.2 * others), f1 / 1.25)
    : f1 / 1.25;
  // ── ⚠ הרצפה מנצחת את ההתאמה, ולא להפך ─────────────────────────────────
  //
  // 1.5 מ״מ ו-1.8 מ״מ אינם עיגול — הם הגבול שמתחתיו הדפסה תרמית ב-203dpi
  // מוציאה כתם. מדבקה שדחסה שמונה שורות לגובה 20 מ״מ הייתה „נכנסת" במספרים
  // ויוצאת בלתי קריאה, וזו בדיוק התלונה שהתחלנו ממנה.
  //
  // לכן כשהרצפה גוברת התוכן **נחתך** ולא מתכווץ: `.bdy` הוא היחיד שמוותר
  // על מקום (`flex: 1 1 auto; overflow: hidden`), והברקוד קבוע אחריו —
  // כלומר מה שאובד הוא השורה האחרונה, ולא הברקוד ולא הכותרת.
  //
  // זה קורה רק בתצורה שהמשרד הרכיב בעצמו: אחרי איחוד השורות, מדבקת לייזר
  // אמיתית היא שלוש שורות, ועל 40×20 נכנסות חמש.
  const f2 = Math.max(1.5, Math.floor(f2raw * 10) / 10);
  return { pad, bcH, f1, f2 };
}

// ── פריסת QR (אלי, 29/09/2026: „יש לי סורק QR") ─────────────────────────
//
// הכותרת (המק״ט) על כל הרוחב למעלה — היא מה שקוראים מרחוק. מתחתיה רצועה:
// השורות המשניות, ולצידן ריבוע QR. השורות המשניות קצרות ואינן צריכות את כל
// הרוחב; Code128 היה צריך, ולכן הוא ישב ברצועה משלו ולקח שליש מהגובה.
//
// ⚠ גודל ה-QR הוא **מספר שלם של נקודות מדפסת למודול** (ראה `labelCode`).
// נבחר הגדול ביותר (6 עד 2 נקודות) שאינו מקטין את הכתב: QR גדול יותר
// נסרק מרחוק יותר, אבל לא על חשבון המק״ט.

/** מה שהפריסה צריכה לדעת על השורות המשניות, בשתי הצורות האפשריות. */
export type SubRows = { count: number; longest: number };

export type QrLayout = {
  pad: number; qr: number; dots: number; gapX: number; gapY: number;
  f1: number; f2: number;
  /** האם השורות המשניות מודפסות כל ערך בשורה משלו (הרצועה צרה). */
  split: boolean;
  /**
   * מדבקה גבוהה (50×70): הכותרת, מתחתיה QR גדול במרכז, ומתחתיו השורות על
   * כל הרוחב. ברצועה צדדית היה נשאר חלל ריק של 40 מ״מ באמצע המדבקה.
   */
  stacked: boolean;
};

/**
 * רוחב תו עברי/לטיני **מודגש** בגופן הממשק, ביחס לגובה הגופן.
 *
 * ⚠ 0.56 נמדד על משקל רגיל, והשם „פלטת בסיס מחוזקת" נחתך ב-„…" על המדבקה
 * המרונדרת: 800 רחב יותר. עדיף גופן קטן בעשירית מ״מ ממילה חתוכה.
 */
const PROP_CHAR = 0.64;

/**
 * גופן משני שכבר קריא היטב ב-203dpi. מעליו אין טעם לגנוב מקום מה-QR:
 * QR גדול יותר נסרק מהר יותר ומרחוק יותר, וגופן של 3 מ״מ לא נקרא טוב יותר
 * מגופן של 2.6.
 */
const F2_OK = 2.6;

export function qrLabelLayout(
  widthMm: number, heightMm: number,
  hero: { rows: number; chars: number },
  joined: SubRows, split: SubRows,
  modules: number,
): QrLayout {
  const pad = Math.max(1, Math.round(Math.min(widthMm, heightMm) * 0.06));
  const W = widthMm - pad * 2;
  const H = heightMm - pad * 2;
  const gapY = 0.8;
  const byWidth = W / (0.62 * Math.max(6, hero.chars));
  const down = (v: number): number => Math.floor(v * 10) / 10;
  const widthFit = (r: SubRows, areaW: number): number => areaW / (PROP_CHAR * Math.max(4, r.longest));

  // ── מדבקה גבוהה: זה מעל זה ──────────────────────────────────────────
  if (heightMm >= widthMm * 1.2) {
    const f1 = Math.min(byWidth, (H * 0.3) / (1.1 * hero.rows));
    const r = joined;
    const f2 = r.count === 0 ? f1 / 1.25 : Math.min(widthFit(r, W), f1 / 1.25, 5);
    const rest = H - 1.1 * f1 * hero.rows - 1.2 * f2 * r.count - gapY * 2;
    let dots = 2;
    for (const d of [8, 7, 6, 5, 4, 3]) {
      if ((modules * d) / 8 <= Math.min(rest, W * 0.8)) { dots = d; break; }
    }
    return {
      pad, qr: (modules * dots) / 8, dots, gapX: 0, gapY,
      f1: Math.max(1.8, down(f1)), f2: Math.max(1.5, down(f2)), split: false, stacked: true,
    };
  }

  type C = { dots: number; qr: number; gapX: number; f1: number; f2: number; split: boolean };
  const cands: C[] = [];
  for (const dots of [6, 5, 4, 3, 2]) {
    const qr = (modules * dots) / 8;
    // אזור שקט: לפחות 4 מודולים בין ה-QR לטקסט — פחות מזה והסורק מתבלבל.
    const gapX = Math.max(1.2, (4 * dots) / 8);
    // 2 נקודות הוא המינימום שמדפסת תרמית מדפיסה — הוא נשאר מועמד תמיד.
    if ((qr > H || qr > W * 0.6) && dots > 2) continue;
    const f1 = Math.min(byWidth, (H - qr - gapY) / (1.1 * hero.rows));
    const bandH = H - 1.1 * f1 * hero.rows - gapY;
    const areaW = W - qr - gapX;
    const fit = (r: SubRows): number => (r.count === 0 ? f1 / 1.25
      : Math.min(bandH / (1.2 * r.count), widthFit(r, areaW), f1 / 1.25));
    const fj = fit(joined);
    const fs = fit(split);
    cands.push({ dots, qr, gapX, f1, f2: Math.max(fj, fs), split: fs > fj });
  }
  // ── הבחירה: ה-QR הגדול ביותר שאינו פוגע בקריאוּת ──────────────────────
  //
  // המק״ט רשאי לוותר עד עשירית מגודלו (5.7 → 5.1 מ״מ אינו נראה לעין מטר
  // מהמדבקה) — תמורת מודול של 3 נקודות במקום 2, שהוא ההבדל בין QR שנסרק
  // ברגע לבין QR שצריך לכוון אליו. השורות המשניות רשאיות לרדת עד `F2_OK`.
  const bestF1 = Math.max(...cands.map((c) => c.f1));
  const bestF2 = Math.max(...cands.map((c) => c.f2));
  const pick = cands.find((c) => c.f1 >= bestF1 * 0.9 && c.f2 >= Math.min(bestF2 * 0.9, F2_OK))
    ?? cands[cands.length - 1]!;
  return {
    pad, qr: pick.qr, dots: pick.dots, gapX: pick.gapX, gapY,
    f1: Math.max(1.8, down(pick.f1)),
    f2: Math.max(1.5, down(pick.f2)),
    split: pick.split, stacked: false,
  };
}

/**
 * מדפיס מדבקה — או סדרה שלהן.
 *
 * הלייזר הוא הסיבה שזו סדרה: קובץ נסט אחד מדווח עשרות חלקים שונים, ומדבקה
 * אחת לכולם היא מדבקה שגויה על 39 מהם. כל רשומה היא מדבקה, וכולן יוצאות
 * בהדפסה אחת — לא 40 חלונות.
 */
/** כמה שורות תופסת הכותרת של הרשומה הזאת, אחרי השבירה ל-14 תווים. */
function heroLineCount(data: ProcessLabelData, rule: ProcessLabelRule): number {
  const first = rule.fields.find((k) => k !== 'barcode' && data[k] != null && data[k] !== '');
  return first ? splitHero(String(data[first])).length : 1;
}

export function printProcessLabel(rule: ProcessLabelRule, input: ProcessLabelData | ProcessLabelData[]): void {
  const list = (Array.isArray(input) ? input : [input]).filter((d) => labelHasContent(rule, d));
  if (list.length === 0) return;
  const w = window.open('', '_blank', 'width=520,height=640');
  if (!w) return;

  const valuesFor = (data: ProcessLabelData): string[] =>
    rule.fields.filter((k) => k !== 'barcode' && data[k] != null && data[k] !== '').map((k) => String(data[k]));
  const rowsFor = (data: ProcessLabelData): string[] => labelRows(valuesFor(data));
  const codeFor = (data: ProcessLabelData): string | null =>
    (rule.fields.includes('barcode') && data.barcode ? String(data.barcode) : null);

  // ⚠ הגופן נגזר מה**מדבקה הצפופה ביותר** בסדרה, ולא מכל אחת לחוד: גליל
  // שבו כל מדבקה בגודל אחר אינו „מותאם", הוא נראה שבור. נסט אחד מדפיס
  // עשרות חלקים, וזה בדיוק המצב שבו זה נראה.
  const maxRows = Math.max(...list.map((d) => rowsFor(d).length));
  const heroChars = Math.max(...list.map((d) => rowsFor(d)[0]?.length ?? 6));
  const anyBarcode = list.some((d) => !!codeFor(d));
  // כמה שורות תופסת הכותרת בצפופה שבסדרה — אותו טיעון כמו `maxRows`.
  const heroRows = Math.max(...list.map((d) => heroLineCount(d, rule)));

  // ── השורות המשניות, בשתי הצורות ─────────────────────────────────────
  //
  // ליד ה-QR הרצועה צרה יותר, ולפעמים ארבע שורות קצרות קריאות יותר משתיים
  // מאוחדות וארוכות. הפריסה מחשבת את שתיהן ובוחרת את הגופן הגדול יותר.
  const subOf = (rows: string[]): SubRows => ({
    count: rows.length, longest: Math.max(0, ...rows.map(visibleLen)),
  });
  const joinedFor = (d: ProcessLabelData): string[] => rowsFor(d).slice(heroLineCount(d, rule));
  const splitFor = (d: ProcessLabelData): string[] => valuesFor(d).slice(1);
  const worst = (f: (d: ProcessLabelData) => string[]): SubRows => list.map((d) => subOf(f(d)))
    .reduce((a, b) => ({ count: Math.max(a.count, b.count), longest: Math.max(a.longest, b.longest) }));
  const modules = Math.max(...list.map((d) => { const c = codeFor(d); return c ? qrModules(c) : 21; }));

  const Q = anyBarcode
    ? qrLabelLayout(rule.widthMm, rule.heightMm, { rows: heroRows, chars: heroChars }, worst(joinedFor), worst(splitFor), modules)
    : null;
  const L = Q ?? labelType(rule.widthMm, rule.heightMm, maxRows, heroChars, false, heroRows);

  const cellFor = (data: ProcessLabelData): string => {
    const code = codeFor(data);
    const hero = heroLineCount(data, rule);
    const heroHtml = rowsFor(data).slice(0, hero)
      .map((line) => `<div class="hd"><bdi>${esc(line)}</bdi></div>`).join('');
    // ── ⚠ שלוש רמות ולא שתיים ─────────────────────────────────────────
    //
    // נמדד על מדבקה אמיתית ב-23/09/2026 — „בחיתוך 1/4 · מדבקה 1/4"
    // ו„פלטת בסיס · ST37" באותו גודל ובאותו משקל בדיוק. הראשון מבדיל בין
    // פיסות **זהות** בערימה, השני הוא רקע: אותו גודל, משקל שונה.
    const sub = (Q?.split ? splitFor(data) : joinedFor(data));
    const subHtml = sub.map((line, i) =>
      `<div class="${i === 0 ? 'lnKey' : 'ln'}"><bdi>${esc(line)}</bdi></div>`).join('');
    if (!Q) {
      return `<div class="pg"><div class="lb"><div class="bdy">${heroHtml}${subHtml}</div></div></div>`;
    }
    const img = code ? `<img class="qr" src="${labelBarcodeSrc(code)}" alt="" />` : '';
    if (Q.stacked) {
      return `<div class="pg"><div class="lb">
      <div class="hero">${heroHtml}</div><div class="mid">${img}</div><div class="sub">${subHtml}</div>
    </div></div>`;
    }
    return `<div class="pg"><div class="lb">
      <div class="hero">${heroHtml}</div>
      <div class="band">
        <div class="sub">${subHtml}</div>
        ${code ? `<img class="qr" src="${labelBarcodeSrc(code)}" alt="" />` : ''}
      </div>
    </div></div>`;
  };

  // העותקים חלים על **כל** מדבקה בסדרה: „2 עותקים" של נסט בן 12 חלקים הם
  // 24 מדבקות, אחת לכל חלק פעמיים — וזה מה שמי שהגדיר 2 התכוון אליו.
  const cells = list
    .flatMap((d) => Array.from({ length: Math.max(1, rule.copies) }, () => cellFor(d)))
    .join('');

  const first = list[0]!;
  const firstText = rule.fields.find((k) => k !== 'barcode' && first[k] != null && first[k] !== '');
  const title = firstText ? String(first[firstText]) : rule.process;

  w.document.write(`<!doctype html><html dir="rtl"><head><meta charset="utf-8">
<title>${esc(title)}</title>
<style>
  @page { size: ${rule.widthMm}mm ${rule.heightMm}mm; margin: 0; }
  html, body { margin: 0; padding: 0; font-family: system-ui, "Segoe UI", Arial, sans-serif;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pg { width: ${rule.widthMm}mm; height: ${rule.heightMm}mm; overflow: hidden; }
  /* שבירה **לפני** כל מדבקה נוספת ולא אחרי כל אחת: אחרי-כולן מבקשת דף
     גם אחרי האחרונה, ומשם הדף הריק ו„Odd pages only“. */
  .pg + .pg { break-before: page; page-break-before: always; }
  .lb { box-sizing: border-box; width: 100%; height: 100%; padding: ${L.pad}mm;
        display: flex; flex-direction: column; }
  .bdy { flex: 1 1 auto; min-height: 0; overflow: hidden; }
  .hero { flex: 0 0 auto; }
  /* הרצועה: השורות המשניות, ולצידן ה-QR — צמוד לתחתית, כך שהחלל הפנוי
     (אם יש) יושב בין המק״ט לרצועה ולא נדחס בתוכה. */
  .band { flex: 1 1 auto; min-height: 0; display: flex; align-items: flex-end;
          gap: ${Q ? Q.gapX : 0}mm; margin-top: ${Q ? Q.gapY : 0}mm; }
  .sub { flex: 1 1 auto; min-width: 0; align-self: stretch; overflow: hidden;
         display: flex; flex-direction: column; justify-content: flex-end; }
  /* מדבקה גבוהה: ה-QR במרכז, והשורות מתחתיו על כל הרוחב. */
  .mid { flex: 1 1 auto; min-height: 0; display: flex; align-items: center; justify-content: center; }
  .lb > .sub { flex: 0 0 auto; }
  /* ⚠ שחור מלא ו-800, וחד-רוחבי לכותרת. „קשה לראות מה כתוב" הוא גם
     ניגודיות ולא רק גודל: מדפסת תרמית ב-203dpi מאבדת גוונים, ו-700 על
     אפור יוצא דהוי. חד-רוחבי כי הכותרת היא מק״ט — 0 ו-O חייבים להיבדל. */
  /* ⚠ שתי שורות כותרת הן מק״ט **אחד** שנשבר, ולכן צמודות. */
  .hd + .hd { margin-top: -0.3mm; }
  .hd { font-family: ui-monospace, "Consolas", "Courier New", monospace;
        font-size: ${L.f1}mm; font-weight: 800; line-height: 1.1; color: #000;
        letter-spacing: -0.02em;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .lnKey { font-size: ${L.f2}mm; line-height: 1.2; color: #000; font-weight: 800;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* ⚠ 600 ולא 500: קו דק מתפרק בהדפסה תרמית לנקודות בודדות — „דהוי". */
  .ln { font-size: ${L.f2}mm; line-height: 1.2; color: #000; font-weight: 600;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* ⚠ מידה מדויקת במ״מ, מספר שלם של נקודות למודול — לא „100%" ולא fill.
     QR מרובע; מתיחתו לרצועה (כמו שעשינו ל-Code128) הורסת אותו. */
  .qr { display: block; flex: 0 0 auto; width: ${Q ? Q.qr : 0}mm; height: ${Q ? Q.qr : 0}mm;
        image-rendering: pixelated; }
</style></head><body>${cells}
<script>
  // מדפיס רק אחרי שהקוד נטען: הדפסה על תמונה שלא הגיעה מוציאה מדבקה
  // בלי קוד, וזה מתגלה על הגליל ולא על המסך.
  (function () {
    var imgs = Array.prototype.slice.call(document.images);
    var left = imgs.length;
    function go() { window.focus(); window.print(); setTimeout(function () { window.close(); }, 400); }
    if (left === 0) { go(); return; }
    imgs.forEach(function (im) {
      if (im.complete) { if (--left === 0) go(); return; }
      im.addEventListener('load', function () { if (--left === 0) go(); });
      // תמונה שנכשלה אינה עוצרת את ההדפסה לנצח — עדיף מדבקה בלי קוד
      // מאשר חלון תקוע שהעובד יסגור וימשיך בלי כלום.
      im.addEventListener('error', function () { if (--left === 0) go(); });
    });
  })();
</script></body></html>`);
  w.document.close();
}
